import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap"
import SiteConfigApi from "../../Api/SiteConfigApi"
import { Toast } from "primereact/toast"
import "./EmailConfig.scss"

const EmailConfig = () => {
  const [sendingConfig, setSendingConfig] = useState({
    email: "",
    password: "",
    host: "",
    port: "",
    secure: false,
  })

  const [receivingConfig, setReceivingConfig] = useState({
    email: "",
  })

  const [isEditingSending, setIsEditingSending] = useState(false)
  const [isEditingReceiving, setIsEditingReceiving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [originalSendingConfig, setOriginalSendingConfig] = useState({})
  const [originalReceivingConfig, setOriginalReceivingConfig] = useState({})
  const toast = React.useRef(null)

  useEffect(() => {
    fetchSiteConfig()
  }, [])

  const fetchSiteConfig = async () => {
    setLoading(true)
    try {
      const response = await SiteConfigApi.getSiteConfig()
      if (response.data.status) {
        const config = response.data.data
        if (config.sendingEmail) {
          setSendingConfig({
            email: config.sendingEmail.email || "",
            password: config.sendingEmail.password || "",
            host: config.sendingEmail.host || "",
            port: config.sendingEmail.port || "",
            secure: config.sendingEmail.secure || false,
          })
          setOriginalSendingConfig({
            email: config.sendingEmail.email || "",
            password: config.sendingEmail.password || "",
            host: config.sendingEmail.host || "",
            port: config.sendingEmail.port || "",
            secure: config.sendingEmail.secure || false,
          })
        }
        if (config.recievingEmail) {
          setReceivingConfig({
            email: config.recievingEmail.email || "",
          })
          setOriginalReceivingConfig({
            email: config.recievingEmail.email || "",
          })
        }
      }
    } catch (error) {
      console.error("Error fetching site config:", error)
      showToast("error", "Error", "Failed to fetch email configuration")
    } finally {
      setLoading(false)
    }
  }

  const handleSendingEdit = () => {
    setIsEditingSending(true)
  }

  const handleReceivingEdit = () => {
    setIsEditingReceiving(true)
  }

  const handleSendingCancel = () => {
    setSendingConfig(originalSendingConfig)
    setIsEditingSending(false)
  }

  const handleReceivingCancel = () => {
    setReceivingConfig(originalReceivingConfig)
    setIsEditingReceiving(false)
  }

  const handleSendingSave = async () => {
    setSaving(true)
    try {
      const response = await SiteConfigApi.updateEmailSiteConfig({
        sending: sendingConfig,
      })
      if (response.data.status) {
        setOriginalSendingConfig({ ...sendingConfig })
        setIsEditingSending(false)
        showToast(
          "success",
          "Success",
          "Sending email configuration updated successfully",
        )
      }
    } catch (error) {
      console.error("Error updating sending config:", error)
      showToast(
        "error",
        "Error",
        "Failed to update sending email configuration",
      )
    } finally {
      setSaving(false)
    }
  }

  const handleReceivingSave = async () => {
    setSaving(true)
    try {
      const response = await SiteConfigApi.updateEmailSiteConfig({
        reciving: receivingConfig,
      })
      if (response.data.status) {
        setOriginalReceivingConfig({ ...receivingConfig })
        setIsEditingReceiving(false)
        showToast(
          "success",
          "Success",
          "Receiving email configuration updated successfully",
        )
      }
    } catch (error) {
      console.error("Error updating receiving config:", error)
      showToast(
        "error",
        "Error",
        "Failed to update receiving email configuration",
      )
    } finally {
      setSaving(false)
    }
  }

  const showToast = (severity, summary, detail) => {
    toast?.current?.show({ severity, summary, detail })
  }

  const handleSendingChange = (field, value) => {
    setSendingConfig(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleReceivingChange = (field, value) => {
    setReceivingConfig(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <Spinner color="primary" />
      </div>
    )
  }

  return (
    <>
      <Toast ref={toast} />
      <Row>
        <Col md={6}>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <CardTitle className="h5 mb-0">
                  Sending Email Configuration
                </CardTitle>
                {!isEditingSending && (
                  <Button color="primary" size="sm" onClick={handleSendingEdit}>
                    Edit
                  </Button>
                )}
              </div>

              <Form>
                <FormGroup>
                  <Label for="sendingEmail">Email</Label>
                  <Input
                    id="sendingEmail"
                    type="email"
                    value={sendingConfig.email}
                    onChange={e => handleSendingChange("email", e.target.value)}
                    disabled={!isEditingSending}
                    placeholder="Enter sending email"
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="sendingPassword">Password</Label>
                  <Input
                    id="sendingPassword"
                    type="password"
                    value={sendingConfig.password}
                    onChange={e =>
                      handleSendingChange("password", e.target.value)
                    }
                    disabled={!isEditingSending}
                    placeholder="Enter email password"
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="sendingHost">Host</Label>
                  <Input
                    id="sendingHost"
                    type="text"
                    value={sendingConfig.host}
                    onChange={e => handleSendingChange("host", e.target.value)}
                    disabled={!isEditingSending}
                    placeholder="Enter SMTP host"
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="sendingPort">Port</Label>
                  <Input
                    id="sendingPort"
                    type="number"
                    value={sendingConfig.port}
                    onChange={e =>
                      handleSendingChange("port", parseInt(e.target.value))
                    }
                    disabled={!isEditingSending}
                    placeholder="Enter SMTP port"
                  />
                </FormGroup>

                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={sendingConfig.secure}
                      onChange={e =>
                        handleSendingChange("secure", e.target.checked)
                      }
                      disabled={!isEditingSending}
                    />
                    Secure Connection (SSL/TLS)
                  </Label>
                </FormGroup>

                {isEditingSending && (
                  <div className="d-flex gap-2 mt-3">
                    <Button
                      color="success"
                      size="sm"
                      onClick={handleSendingSave}
                      disabled={saving}
                    >
                      {saving ? <Spinner size="sm" /> : "Save"}
                    </Button>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={handleSendingCancel}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </Form>
            </CardBody>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <CardTitle className="h5 mb-0">
                  Receiving Email Configuration
                </CardTitle>
                {!isEditingReceiving && (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleReceivingEdit}
                  >
                    Edit
                  </Button>
                )}
              </div>

              <Form>
                <FormGroup>
                  <Label for="receivingEmail">Email</Label>
                  <Input
                    id="receivingEmail"
                    type="email"
                    value={receivingConfig.email}
                    onChange={e =>
                      handleReceivingChange("email", e.target.value)
                    }
                    disabled={!isEditingReceiving}
                    placeholder="Enter receiving email"
                  />
                </FormGroup>

                {isEditingReceiving && (
                  <div className="d-flex gap-2 mt-3">
                    <Button
                      color="success"
                      size="sm"
                      onClick={handleReceivingSave}
                      disabled={saving}
                    >
                      {saving ? <Spinner size="sm" /> : "Save"}
                    </Button>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={handleReceivingCancel}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default EmailConfig
